import { useState, useEffect } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { colors } from "../../../theme/palette";
import { GoStarIcon } from "../../../assets/icons/MUI/ExploreIcons";
import { UserInfo } from "../../../types";
import { getUsersForExploreGetStars } from "../../../api/services/users";

import UserCard, { UserCardSkeleton } from "./UserCard";
import APP_ROUTES from "../../../routers/routeConstants";

const GoStar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [goStarList, setGoStarList] = useState<UserInfo[]>([]);
  const [offset, setOffset] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const fetchGoStarData = async (currentOffset: number) => {
    setIsFetching(true);
    try {
      const usersListing = await getUsersForExploreGetStars(currentOffset);
      setGoStarList(usersListing.items || []);
    } catch (error) {
      console.error("Error fetching GoStar data:", error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchGoStarData(offset);
  }, [offset]);

  const handleRemoveUser = (id: number | null) => {
    if (id === null) return;

    const updatedList = goStarList.filter((user) => user.id !== id);
    setGoStarList(updatedList);

    if (updatedList.length === 0) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid #EAECF0",
        background: colors.White,
        height: "fit-content",
        minHeight: "402px",
        padding: "18px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            marginBottom: "27px",
          }}
        >
          <GoStarIcon />
          <Typography
            sx={{
              color: colors.Black100,
              fontFamily: "Inter",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            {t("pages.explore.goStar")}
          </Typography>
        </Box>
        <Link
          onClick={() => navigate(APP_ROUTES.GO_STAR)}
          sx={{
            color: colors.Green100,
            fontFamily: "Inter",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "24px",
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          {t("pages.explore.seeStars")}
        </Link>
      </Box>
      <Box
        sx={{
          display: "flex",
          columnGap: "16px",
          overflowX: "auto",
          width: "100%",
          paddingBottom: "10px",
        }}
      >
        {isFetching && (
          <Box sx={{ display: "flex", columnGap: "16px" }}>
            {[...Array(10)].map((_, idx) => (
              <UserCardSkeleton key={idx} />
            ))}
          </Box>
        )}
        {goStarList.length === 0 && !isFetching && "No data..."}
        {goStarList.length > 0 && !isFetching && (
          <Box sx={{ display: "flex", columnGap: "16px" }}>
            {goStarList.map((user: UserInfo) => {
              return <UserCard key={user.id} data={user} handleRemoveUser={handleRemoveUser} />;
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GoStar;
